import parser from 'any-date-parser';

import { Button, Group, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { closeAllModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';

import { useLazyGetCustomersXlsxQuery } from '@api/customer.api';
import { isApiError } from '@api/index';

import MarketCategoriesSelector from './MarketCategoriesSelector';

export default function GetCustomersXlsxFilters() {
  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    startDate: null as string | null,
    endDate: null as string | null,
    marketCategories: [] as string[],
  };

  const form = useForm({ initialValues });

  // ==========================================================================
  // Api
  // ==========================================================================
  const [getCustomersXlsx, { isLoading }] = useLazyGetCustomersXlsxQuery();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleSubmit = async (values: typeof initialValues) => {
    try {
      const blob = await getCustomersXlsx({
        startDate: values.startDate
          ? new Date(values.startDate).toISOString()
          : undefined,
        endDate: values.endDate
          ? new Date(values.endDate).toISOString()
          : undefined,
        categoriesIds: values.marketCategories,
      }).unwrap();

      const alink = document.createElement('a');
      alink.href = window.URL.createObjectURL(blob);
      alink.download = 'PG Clienti.xlsx';
      alink.click();
      alink.remove();

      closeAllModals();
    } catch (e) {
      if (isApiError(e)) {
        showNotification({
          title: 'Errore',
          message: e.data.message,
          color: 'red',
        });
      } else {
        showNotification({
          title: 'Errore inatteso',
          message: 'Si è verificato un errore.',
          color: 'red',
        });
      }
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <MarketCategoriesSelector
        label="Filtra per mercati di interesse"
        placeholder="Tutti i mercati"
        value={form.values.marketCategories}
        onChange={(value) => form.setFieldValue('marketCategories', value)}
      />
      <Text size="sm" mt="md">
        Filtra per data di registrazione
      </Text>
      <Group wrap="nowrap">
        <DateInput
          label="Da"
          placeholder="Nessuna data di inizio"
          valueFormat="DD/MM/YYYY"
          locale="it"
          maxDate={new Date()}
          dateParser={(value) => parser.fromString(value, 'it')}
          value={form.values.startDate}
          onChange={(value) => form.setFieldValue('startDate', value)}
        />
        <DateInput
          label="A"
          placeholder="Nessuna data di fine"
          valueFormat="DD/MM/YYYY"
          locale="it"
          minDate={form.values.startDate || undefined}
          maxDate={new Date()}
          dateParser={(value) => parser.fromString(value, 'it')}
          value={form.values.endDate}
          onChange={(value) => form.setFieldValue('endDate', value)}
        />
      </Group>
      <Group justify="flex-end">
        <Button type="submit" mt="md" loading={isLoading}>
          Scarica
        </Button>
      </Group>
    </form>
  );
}
